import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { useCart, inr } from "../context/CartContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ProductCatalog = ({ category, setCategory }) => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const { add } = useCart();

  useEffect(() => {
    const load = async () => {
      try {
        const [p, c] = await Promise.all([
          axios.get(`${API}/products`),
          axios.get(`${API}/categories`),
        ]);
        setProducts(p.data);
        setCategories(c.data);
      } catch (e) {
        toast.error("Could not load the collection. Please refresh.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const visible =
    category === "All" ? products : products.filter((p) => p.category === category);

  const handleAdd = (p) => {
    add(p);
    toast.success(`${p.name} added to your bag`);
  };

  return (
    <section
      id="shop"
      data-testid="product-catalog-section"
      className="py-28 md:py-40 bg-[#FBF9F6]"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-12 gap-6 mb-14 md:mb-20">
          <motion.h2
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8 }}
            className="col-span-12 md:col-span-7 font-serif-d font-light text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#2D1B36]"
          >
            The <span className="italic text-[#C5A059]">collection</span>, delivered
            to your door
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="col-span-12 md:col-span-4 md:col-start-9 text-base leading-relaxed text-[#514357] self-end"
          >
            Order online and pay securely with UPI, card or netbanking. We'll
            call to confirm before your parcel leaves Court Road.
          </motion.p>
        </div>

        <div
          data-testid="category-filters"
          className="flex flex-wrap gap-3 mb-14 md:mb-16"
        >
          {["All", ...categories].map((c) => (
            <button
              key={c}
              data-testid={`filter-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              onClick={() => setCategory(c)}
              className={`font-accent text-[11px] tracking-[0.2em] uppercase px-5 py-2.5 border transition-colors duration-300 ${
                category === c
                  ? "bg-[#4A2B58] text-[#FBF9F6] border-[#4A2B58]"
                  : "bg-transparent text-[#4A2B58] border-[#C5A059]/50 hover:bg-[#C5A059]/10"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        {loading ? (
          <div
            data-testid="catalog-loading"
            className="py-24 text-center font-serif-d italic text-2xl text-[#8A818F]"
          >
            Unrolling the shelves…
          </div>
        ) : (
          <motion.div layout className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-14 md:gap-x-10">
            <AnimatePresence mode="popLayout">
              {visible.map((p, i) => (
                <motion.article
                  layout
                  key={p.id}
                  data-testid={`product-card-${p.id}`}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.97 }}
                  transition={{ duration: 0.5, delay: Math.min(i * 0.04, 0.4) }}
                  className="group"
                >
                  <div
                    className={`relative overflow-hidden bg-[#F2EFEB] ${
                      p.featured ? "arch-frame" : ""
                    } aspect-[3/4]`}
                  >
                    <Link to={`/product/${p.id}`} data-testid={`product-link-${p.id}`} aria-label={p.name} className="absolute inset-0 block">
                      <img
                        src={p.image}
                        alt={p.name}
                        loading="lazy"
                        className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                      />
                    </Link>
                    <button
                      onClick={() => handleAdd(p)}
                      data-testid={`add-to-cart-${p.id}`}
                      className="absolute z-10 bottom-0 left-0 right-0 bg-[#2D1B36]/90 backdrop-blur text-[#FBF9F6] font-accent text-[11px] tracking-[0.25em] uppercase py-3.5 flex items-center justify-center gap-2 translate-y-full group-hover:translate-y-0 transition-transform duration-400 ease-out hover:bg-[#C5A059] hover:text-[#2D1B36]"
                    >
                      <Plus size={13} /> Add to Bag
                    </button>
                  </div>
                  <div className="mt-4 flex items-start justify-between gap-3">
                    <div>
                      <h3 className="font-serif-d text-lg md:text-xl leading-snug text-[#2D1B36]">
                        <Link to={`/product/${p.id}`} className="hover:text-[#C5A059] transition-colors duration-300">
                          {p.name}
                        </Link>
                      </h3>
                      <p className="font-accent text-[10px] tracking-[0.2em] uppercase text-[#8A818F] mt-1">
                        {p.category}
                      </p>
                    </div>
                    <div className="text-right shrink-0">
                      <span
                        data-testid={`product-price-${p.id}`}
                        className="block text-base font-medium text-[#4A2B58]"
                      >
                        {inr(p.price)}
                      </span>
                      <span className="block text-[10px] text-[#8A818F]">{p.unit}</span>
                    </div>
                  </div>
                </motion.article>
              ))}
            </AnimatePresence>
          </motion.div>
        )}
      </div>
    </section>
  );
};
